import React, { useState } from 'react';
import {
  Box, Container, Typography, Paper, Grid, Card, CardContent, Stack, LinearProgress
} from '@mui/material';
import {
  TrendingUp as TrendingIcon, Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon, Error as ErrorIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { Ticket } from '../types';
import { mockTickets } from '../services/mockData';
import TicketDetails from './TicketDetails';
import TicketForm from './TicketForm';

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, onClick }) => (
  <Card
    sx={{
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': onClick ? { transform: 'translateY(-4px)', boxShadow: 4 } : {}
    }}
    onClick={onClick}
  >
    <CardContent>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${color}.lighter`, color: `${color}.main`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>{title}</Typography>
          <Typography variant="h4" fontWeight="bold">{value}</Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [tickets] = useState<Ticket[]>(mockTickets);

  // Modal state
  const [detailTicketId, setDetailTicketId] = useState<string | null>(null);
  const [formTicketId, setFormTicketId] = useState<string | null | undefined>(undefined);

  const stats = {
    total: tickets.length,
    new: tickets.filter(t => t.status === 'new').length,
    open: tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
    closed: tickets.filter(t => t.status === 'closed').length,
    critical: tickets.filter(t => t.priority === 'critical').length,
    high: tickets.filter(t => t.priority === 'high').length,
    unassigned: tickets.filter(t => !t.assignee).length
  };

  const activeTickets = stats.new + stats.open + stats.inProgress;
  const completionRate = stats.total > 0 ? ((stats.resolved + stats.closed) / stats.total) * 100 : 0;

  const categoryStats = tickets.reduce((acc, ticket) => {
    acc[ticket.category] = (acc[ticket.category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const recentTickets = [...tickets]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 5);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
          Support Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Overview of support tickets and system health
        </Typography>
      </Box>

      {/* Main Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Tickets" value={stats.total} icon={<AssignmentIcon />} color="primary"
            onClick={() => navigate('/app/yoursupport/tickets')} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Active Tickets" value={activeTickets} icon={<TrendingIcon />} color="warning"
            onClick={() => navigate('/app/yoursupport/tickets')} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Resolved" value={stats.resolved} icon={<CheckCircleIcon />} color="success"
            onClick={() => navigate('/app/yoursupport/tickets')} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Critical Priority" value={stats.critical} icon={<ErrorIcon />} color="error"
            onClick={() => navigate('/app/yoursupport/tickets')} />
        </Grid>
      </Grid>

      {/* Status Breakdown */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Status Overview</Typography>
            <Stack spacing={2} sx={{ mt: 2 }}>
              {[
                { label: 'New', value: stats.new, color: 'info' },
                { label: 'Open', value: stats.open, color: 'primary' },
                { label: 'In Progress', value: stats.inProgress, color: 'warning' },
                { label: 'Resolved', value: stats.resolved, color: 'success' },
              ].map(item => (
                <Box key={item.label}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">{item.label}</Typography>
                    <Typography variant="body2" fontWeight="bold">{item.value}</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={stats.total > 0 ? (item.value / stats.total) * 100 : 0}
                    sx={{ height: 8, borderRadius: 1 }}
                    color={item.color as any}
                  />
                </Box>
              ))}
            </Stack>
            <Box sx={{ mt: 3, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>Completion Rate</Typography>
              <Typography variant="h5" fontWeight="bold" color="success.main">{completionRate.toFixed(1)}%</Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Priority Distribution</Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'error.lighter', borderRadius: 1 }}>
                  <Typography variant="h4" fontWeight="bold" color="error.main">{stats.critical}</Typography>
                  <Typography variant="body2" color="text.secondary">Critical</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.lighter', borderRadius: 1 }}>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">{stats.high}</Typography>
                  <Typography variant="body2" color="text.secondary">High</Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Categories</Typography>
            <Stack spacing={1.5} sx={{ mt: 2 }}>
              {Object.entries(categoryStats)
                .sort(([, a], [, b]) => b - a)
                .map(([category, count]) => (
                  <Box key={category} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">{category}</Typography>
                    <Typography variant="body2" fontWeight="bold" color="primary">{count}</Typography>
                  </Box>
                ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Tickets */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Recent Tickets</Typography>
        <Stack spacing={2} sx={{ mt: 2 }}>
          {recentTickets.map(ticket => (
            <Box
              key={ticket.id}
              sx={{
                p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider',
                cursor: 'pointer', transition: 'all 0.2s',
                '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' }
              }}
              onClick={() => setDetailTicketId(ticket.id)}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>{ticket.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{ticket.id} &bull; {ticket.category}</Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    px: 1, py: 0.5, borderRadius: 1, fontWeight: 'bold',
                    bgcolor: ticket.priority === 'critical' ? 'error.lighter' : 'warning.lighter',
                    color: ticket.priority === 'critical' ? 'error.main' : 'warning.main',
                  }}
                >
                  {ticket.priority.toUpperCase()}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      </Paper>

      {/* Detail Modal */}
      <TicketDetails
        isOpen={!!detailTicketId}
        ticketId={detailTicketId}
        onClose={() => setDetailTicketId(null)}
        onEdit={(id) => { setDetailTicketId(null); setFormTicketId(id); }}
      />

      {/* Form Modal */}
      <TicketForm
        isOpen={formTicketId !== undefined}
        ticketId={formTicketId}
        onClose={() => setFormTicketId(undefined)}
        onSuccess={() => { /* TODO: refresh */ }}
      />
    </Container>
  );
};

export default Dashboard;
